// src/pages/pentest/dashboard/PentestApiDashboard.jsx
import React, { useEffect, useState } from 'react';
import {
  Box, Card, CardContent, Typography, Grid, CircularProgress,
  TableContainer, Table, TableHead, TableRow, TableCell, TableBody,
  Paper, Chip
} from '@mui/material';
import { useParams, useNavigate } from 'react-router-dom';
import { Bar } from 'react-chartjs-2';
import 'chart.js/auto';
import Nav from './nav';
import { alpha , darken, lighten} from '@mui/material/styles';
import { BASEURL ,PRIMARYCOLOR,SECONDARYCOLOR,logoURL,COMPANY } from '../../Constants';

// ───── icons ────────────────────────────────────────────────
import ApiIcon            from '@mui/icons-material/Api';
import BugReportIcon      from '@mui/icons-material/BugReport';
import SecurityIcon       from '@mui/icons-material/Security';
import PriorityHighIcon   from '@mui/icons-material/PriorityHigh';
import WarningAmberIcon   from '@mui/icons-material/WarningAmber';
import ReportProblemIcon  from '@mui/icons-material/ReportProblem';
import InfoIcon           from '@mui/icons-material/Info';

// ───── grade helpers ───────────────────────────────────────
const getGrade = (score) => {
  if (score == null) return '?';
  if (score < 60) return 'F';
  if (score < 70) return 'D';
  if (score < 80) return 'C';
  if (score < 90) return 'B';
  if (score <= 95) return 'A-';
  return 'A+';
};
const gradePalette = {
  'A+':'#4caf50', 'A-':'#4caf50', B:'#2196f3',
  C:'#ff9800', D:'#ffeb3b', F:'#f44336'
};
const gradeMessages = {
  'A+':'Outstanding security posture! No critical issues found.',
  'A-':'Great job! Keep up the good work.',
  B:'Good performance, but address minor issues.',
  C:'Fair; several issues need attention.',
  D:'Poor; important vulnerabilities detected.',
  F:'Critical issues! Immediate remediation required.'
};

// ───── severity colour & icon map ──────────────────────────
const sevMeta = {
  Critical:{ color:'#800080', Icon:PriorityHighIcon },
  High    :{ color:'#f44336', Icon:PriorityHighIcon },
  Medium  :{ color:'#ff9800', Icon:WarningAmberIcon },
  Low     :{ color:'#2196f3', Icon:ReportProblemIcon },
  Info    :{ color:'#4caf50', Icon:InfoIcon }
};

const PentestApiDashboard = () => {
  const { pentest_id } = useParams();
  const navigate       = useNavigate();

  const [loading,  setLoading]  = useState(true);
  const [exploits, setExploits] = useState([]);
  const [stats,    setStats]    = useState(null);

  // ─── fetch minimal data (stats + exploits) ───────────────
  useEffect(() => {
    (async () => {
      const token = sessionStorage.getItem('token');
      if (!token) return navigate('/');
      try {
        const headers = { Authorization:`Bearer ${token}` };
        const base    = `${BASEURL}/pentests/api`;
        const [exR, stR] = await Promise.all([
          fetch(`${base}/exploits?pentest_id=${pentest_id}`, { headers }),
          fetch(`${base}/stats?pentest_id=${pentest_id}`,    { headers })
        ]);
        const [{ exploits:exs }, { stats:st }] = await Promise.all([exR.json(), stR.json()]);
        setExploits(exs); setStats(st);
      } catch (e) { console.error(e); }
      finally     { setLoading(false);  }
    })();
  }, [pentest_id, navigate]);

  if (loading || !stats) {
    return (
      <Box sx={{ display:'flex', flexDirection:'column', alignItems:'center', mt:4 }}>
        <CircularProgress/>
        <Typography sx={{ mt:2 }}>Loading API Pentest Dashboard…</Typography>
      </Box>
    );
  }

  // ─── derived values ──────────────────────────────────────
  const grade        = getGrade(stats.risk_score);
  const gradeColor   = gradePalette[grade] || '#9e9e9e';
  const gradeMessage = gradeMessages[grade] || '';

  const sevCounts = exploits.reduce((a,e)=>{
    const k=(e.severity||'').toLowerCase(); a[k]=(a[k]||0)+1; return a;
  }, { critical:0, high:0, medium:0, low:0, info:0 });

  // original simple charts
  const severityChartData = {
    labels:['Critical','High','Medium','Low','Info'],
    datasets:[{ label:'Exploits by Severity', data:Object.values(sevCounts), borderWidth:0, backgroundColor:[
      lighten(sevMeta.Critical.color,0.4), lighten(sevMeta.High.color,0.4), lighten(sevMeta.Medium.color,0.4), lighten(sevMeta.Low.color,0.4), lighten(sevMeta.Info.color,0.4)
    ]}]
  };
  const methodChartData = {
    labels:['GET','POST','PUT','DELETE','PATCH'],
    datasets:[{ label:'Methods Count', data:[
      stats.total_get_methods, stats.total_post_methods, stats.total_put_methods,
      stats.total_delete_methods, stats.total_patch_methods], borderWidth:0, backgroundColor:lighten(PRIMARYCOLOR,0.5) }]
  };

  // top-5 endpoints table
  const endpointRisk = exploits.reduce((a,e)=>{
    const ep=e.impacted_assets || 'Unknown';
    if(!a[ep]) a[ep]={total:0,critical:0,high:0,medium:0,low:0,info:0};
    a[ep].total++; const s=(e.severity||'').toLowerCase(); if(a[ep][s]!=null) a[ep][s]++; return a;
  },{});
  const topEndpoints = Object.entries(endpointRisk)
        .map(([ep,c])=>({endpoint:ep,...c}))
        .sort((a,b)=>b.total-a.total).slice(0,5);

  // ─── metric-card config (icons + colours) ────────────────
  const metrics = [
    { label:'Endpoints', value:stats.total_endpoints,   Icon:ApiIcon },
    { label:'Exploits',  value:exploits.length,         Icon:BugReportIcon },
    { label:'Risk Score',value:stats.risk_score ?? 'N/A', Icon:SecurityIcon },
    ...Object.entries(sevCounts).map(([k,v])=>{
      const label = k.charAt(0).toUpperCase()+k.slice(1);
      const {color,Icon} = sevMeta[label] || {};
      return { label, value:v, color, Icon };
    })
  ];

  // ─── render ───────────────────────────────────────────────
  return (
    <Box sx={{ display:'flex', flexDirection:'column', minHeight:'100vh' }}>
      <Nav/>
      <Box sx={{ p:3, flexGrow:1 }}>
        <Typography variant="h4" sx={{ mb:3, fontWeight:'bold' }}>
          API Pentest Dashboard
        </Typography>

        {/* ── grade + metric cards ───────────────────────── */}
        <Grid container spacing={2} sx={{ mb:4 }}>
          {/* grade card */}
          <Grid item xs={12} sm={6} md={4} lg={3}>
            <Card sx={{
              height:'100%',
              bgcolor:alpha(gradeColor,0.1),
              borderLeft:`8px solid ${gradeColor}`
            }}>
              <CardContent sx={{ p:2, display:'flex', flexDirection:'column',
                                 alignItems:'center', justifyContent:'center' }}>
                <Typography variant="h1" sx={{ color:gradeColor, fontWeight:800, lineHeight:1 }}>
                  {grade}
                </Typography>
                <Typography variant="subtitle2" sx={{ mt:1, textAlign:'center' }}>
                  {gradeMessage}
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          {/* metric cards */}
          {metrics.map(({label,value,color,Icon})=>(
            <Grid key={label} item xs={6} sm={4} md={3} lg={2}>
              <Card sx={{ height:'100%', borderTop:color?`4px solid ${color}`:undefined }}>
                <CardContent sx={{ p:1.5, display:'flex', alignItems:'center', gap:1 }}>
                  <Icon sx={{ fontSize:28, color:color||'text.secondary' }}/>
                  <Box sx={{ lineHeight:1 }}>
                    <Typography variant="h6" sx={{ fontWeight:700 }}>
                      {value}
                    </Typography>
                    <Typography variant="caption">{label}</Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        {/* ── charts ─────────────────────────────────────── */}
        <Grid container spacing={2} sx={{ mb:4 }}>
          <Grid item xs={12} md={6}>
            <Card sx={{ p:2 }}>
              <Typography variant="h6" sx={{ mb:2 }}>Exploits by Severity</Typography>
              <Bar data={severityChartData} />
            </Card>
          </Grid>
          <Grid item xs={12} md={6}>
            <Card sx={{ p:2 }}>
              <Typography variant="h6" sx={{ mb:2 }}>Request Method Distribution</Typography>
              <Bar data={methodChartData}/>
            </Card>
          </Grid>
        </Grid>

        {/* ── top-5 endpoints table ─────────────────────── */}
        <Card sx={{ p:2, mb:4 }}>
          <Typography variant="h6" sx={{ mb:2 }}>Top 5 Endpoints at Risk</Typography>
          <TableContainer component={Paper} sx={{ maxHeight:340 }}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow sx={{ '& th':{ background:PRIMARYCOLOR, color:'#fff',
                                          borderBottom:'1px solid #30363d' }}}>
                  <TableCell>Endpoint</TableCell>
                  <TableCell>Total</TableCell>
                  <TableCell>Critical</TableCell><TableCell>High</TableCell>
                  <TableCell>Medium</TableCell><TableCell>Low</TableCell><TableCell>Info</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {topEndpoints.map(r=>(
                  <TableRow key={r.endpoint} hover
                            sx={{ '&:nth-of-type(odd)':{ background:'#f9f9f9' }}}>
                    <TableCell sx={{ fontWeight:500 }}>{r.endpoint}</TableCell>
                    <TableCell sx={{ fontWeight:700 }}>{r.total}</TableCell>
                    {['critical','high','medium','low','info'].map(sev=>{
                      const count = r[sev]; const label = sev.charAt(0).toUpperCase()+sev.slice(1);
                      return (
                        <TableCell key={sev} align="center">
                          {count ? (
                            <Chip label={count} size="small"
                              sx={{ bgcolor:sevMeta[label].color, color:'#fff', minWidth:36 }}/>
                          ) : '—'}
                        </TableCell>
                      );
                    })}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Card>
      </Box>
    </Box>
  );
};

export default PentestApiDashboard;
